/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './ProjectChat.css';

const CATEGORY_LABELS = {
  all: 'All',
  application_project: 'Application / Project',
  assignment: 'Assignments',
  invoice: 'Invoices',
  request: 'Requests',
  team: 'Team',
  renewal: 'Renewal',
  general: 'General'
};

const EVENT_TYPE_LABELS = {
  application_submitted: 'Application Submitted',
  application_approved: 'Application Approved',
  application_rejected: 'Application Rejected',
  revision_requested: 'Revision Requested',
  application_revised: 'Application Revised',
  project_started: 'Project Started',
  project_completed: 'Project Completed',
  project_terminated: 'Project Terminated',
  chamber_assigned: 'Compartment Assigned',
  chamber_updated: 'Compartment Updated',
  chamber_completed: 'Compartment Completed',
  chamber_deleted: 'Compartment Removed',
  resource_assigned: 'Resource Assigned',
  resource_updated: 'Resource Updated',
  invoice_created: 'Invoice Created',
  invoice_edited: 'Invoice Edited',
  invoice_deleted: 'Invoice Deleted',
  invoice_status_changed: 'Invoice Status Changed',
  invoice_billed: 'Invoice Sent to Team',
  invoice_signed: 'Invoice Signed',
  invoice_disputed: 'Invoice Disputed',
  invoice_dispute_resolved: 'Invoice Re-sent for Signing',
  payment_recorded: 'Payment Recorded',
  request_submitted: 'Request Submitted',
  request_completed: 'Request Completed',
  request_status_changed: 'Request Status Changed',
  request_client_comment: 'Request Reply',
  request_rejected: 'Request Rejected',
  // Legacy: requests used to be approved rather than completed.
  request_approved: 'Request Completed',
  team_member_added: 'Member Added',
  team_member_removed: 'Member Removed',
  team_role_changed: 'Role Changed',
  invitation_accepted: 'Invitation Accepted',
  invitation_declined: 'Invitation Declined',
  project_renewal_confirmation: 'Renewal Confirmation',
  project_renewed: 'Project Renewed',
  general_comment: 'Comment'
};

const getSystemTextClass = (category, event_type) => {
  if (event_type?.includes('approved') || event_type?.includes('started') || event_type?.includes('renewed')) return 'approval';
  if (event_type?.includes('rejected') || event_type?.includes('terminated') || event_type?.includes('deleted')) return 'rejection';
  if (category === 'assignment') return 'assignment';
  if (category === 'invoice') return 'invoice';
  if (category === 'request') return 'request';
  if (category === 'team') return 'team';
  if (category === 'renewal') return 'renewal';
  return '';
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateLabel = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

/**
 * @param {string} channel - 'activity' (read-only log) or 'team_chat' (team messaging)
 * @param {Array} projectUsers - project.users array, used to show project roles in team chat
 */
const ProjectChat = ({
  projectId,
  user,
  maxHeight = '600px',
  refreshTrigger,
  channel = 'activity',
  projectUsers = [],
  // Opening a project's chat normally clears that project's notifications too.
  // Dashboard panels switch this off so browsing chats does not wipe unrelated
  // project notifications out of the Notification Center.
  markProjectNotificationsRead = true,
  allowComments = false,
  commentPlaceholder,
  hideTeamReminder = false
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const initialLoadRef = useRef(true);

  const isActivityChannel = channel === 'activity';
  const canPost = allowComments || !isActivityChannel;

  const getProjectRole = (senderId) => {
    if (!senderId || !projectUsers.length) return null;
    const entry = projectUsers.find(u => {
      const uid = u.user?._id || u.user;
      return uid === senderId || (uid && uid.toString() === senderId.toString());
    });
    return entry?.role?.role || entry?.role || null;
  };

  const fetchMessages = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: 50, channel });
      if (activeCategory !== 'all') params.append('category', activeCategory);

      const response = await axios.get(`/api/projects/${projectId}/chat?${params.toString()}`);
      const data = response.data;

      if (append) {
        setMessages(prev => [...data.messages, ...prev]);
      } else {
        setMessages(data.messages);
      }

      setTotalCount(data.totalCount);
      setUnreadCount(data.unreadCount);
      setHasMore(data.hasMore);
      setPage(pageNum);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [projectId, activeCategory, channel]);

  useEffect(() => {
    if (projectId) {
      initialLoadRef.current = true;
      setPage(1);
      fetchMessages(1);
    }
  }, [projectId, activeCategory, fetchMessages]);

  useEffect(() => {
    if (projectId && refreshTrigger != null && refreshTrigger > 0) {
      fetchMessages(1);
    }
  }, [projectId, refreshTrigger, fetchMessages]);

  useEffect(() => {
    if (initialLoadRef.current && messages.length > 0 && !loading) {
      const container = messagesContainerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
      initialLoadRef.current = false;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!loadMoreSentinelRef.current || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore) {
          const container = messagesContainerRef.current;
          const prevHeight = container?.scrollHeight || 0;
          fetchMessages(page + 1, true).then(() => {
            requestAnimationFrame(() => {
              if (container) {
                container.scrollTop = container.scrollHeight - prevHeight;
              }
            });
          });
        }
      },
      { root: messagesContainerRef.current, threshold: 0.1 }
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, page, fetchMessages]);

  useEffect(() => {
    if (!loading && messages.length > 0 && projectId) {
      const unreadIds = messages
        .filter(m => !m.read_by?.some(r => r.user === user?._id))
        .map(m => m._id);

      if (unreadIds.length > 0) {
        axios.patch(`/api/projects/${projectId}/chat/read`, { messageIds: unreadIds, channel })
          .catch(err => console.error('Error marking messages as read:', err));
      }

      if (markProjectNotificationsRead) {
        axios.patch(`/api/notifications/read-project/${projectId}`)
          .catch(() => {});
      }
    }
  }, [loading, messages, projectId, user, channel, markProjectNotificationsRead]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;

    try {
      setSubmitting(true);
      await axios.post(`/api/projects/${projectId}/chat`, {
        comment: newComment.trim(),
        channel
      });
      setNewComment('');
      await fetchMessages(1);
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const groupedMessages = [];
  let lastDate = null;
  messages.forEach(msg => {
    const msgDate = new Date(msg.createdAt).toDateString();
    if (msgDate !== lastDate) {
      groupedMessages.push({ type: 'date', date: msg.createdAt });
      lastDate = msgDate;
    }
    groupedMessages.push({ type: 'message', data: msg });
  });

  const renderMetadata = (metadata) => {
    if (!metadata || typeof metadata !== 'object') return null;
    const tags = [];
    if (metadata.chamber_name) tags.push(`Compartment: ${metadata.chamber_name}`);
    if (metadata.resource_name) tags.push(`Resource: ${metadata.resource_name}`);
    if (metadata.quantity !== undefined) tags.push(`Qty: ${metadata.quantity}`);
    if (metadata.rate) tags.push(`Rate: ${metadata.rate}`);
    if (metadata.invoice_id) tags.push(`Invoice: ${metadata.invoice_id}`);
    if (metadata.amount !== undefined) tags.push(`$${Number(metadata.amount).toFixed(2)}`);
    if (metadata.payment_mode) tags.push(metadata.payment_mode);
    if (metadata.request_type) tags.push(metadata.request_type);
    if (metadata.request_id) tags.push(metadata.request_id);
    if (metadata.member_name) tags.push(metadata.member_name);
    if (metadata.new_role) tags.push(`Role: ${metadata.new_role}`);
    if (metadata.previous_status && metadata.new_status) {
      tags.push(`${metadata.previous_status} → ${metadata.new_status}`);
    }

    if (tags.length === 0) return null;
    return (
      <div className="chat-metadata">
        {tags.map((tag, i) => (
          <span key={i} className="chat-metadata-tag">{tag}</span>
        ))}
      </div>
    );
  };

  const renderMessage = (msg) => {
    const isUnread = !msg.read_by?.some(r => r.user === user?._id);
    const senderRole = msg.sender_role || 'system';
    const readCount = msg.read_by?.length || 0;

    const displayRole = (!isActivityChannel && msg.sender)
      ? (getProjectRole(msg.sender) || senderRole)
      : senderRole;

    return (
      <div key={msg._id} className={`chat-message ${isUnread ? 'unread' : ''}`}>
        <div className={`chat-avatar ${senderRole}`}>
          {senderRole === 'system' ? '⚙' : getInitials(msg.sender_name)}
        </div>
        <div className="chat-message-body">
          <div className="chat-message-header">
            <span className="chat-sender-name">
              {msg.sender_name || 'System'}
            </span>
            <span className={`chat-role-badge ${senderRole}`}>
              {displayRole}
            </span>
            {msg.event_type && msg.event_type !== 'general_comment' && (
              <span className={`chat-event-badge ${msg.category}`}>
                {EVENT_TYPE_LABELS[msg.event_type] || msg.event_type}
              </span>
            )}
            {msg.visibility === 'admin_only' && (
              <span
                className="chat-admin-only-badge"
                title="Only admins can see this entry. The project team is not notified."
              >
                Admins only
              </span>
            )}
            <span className="chat-timestamp">
              {formatTime(msg.createdAt)}
            </span>
          </div>

          {msg.system_text && (
            <div className={`chat-system-text ${getSystemTextClass(msg.category, msg.event_type)}`}>
              {msg.system_text}
            </div>
          )}

          {msg.comment && (
            <div className="chat-comment-text">{msg.comment}</div>
          )}

          {renderMetadata(msg.metadata)}

          {readCount > 1 && (
            <div className="chat-read-receipt" title={
              msg.read_by
                ?.filter(r => r.user !== user?._id)
                .map(() => '')
                .join('')
            }>
              ✓✓ Seen by {readCount - 1}
            </div>
          )}
        </div>
      </div>
    );
  };

  const emptyMessage = isActivityChannel
    ? 'No activity updates yet.'
    : 'No messages yet. Start the conversation!';

  return (
    <div className="project-chat" style={{ maxHeight }}>
      {isActivityChannel && (
        <div className="chat-toolbar">
          <select
            className="chat-category-select"
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
          >
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          {unreadCount > 0 && (
            <span className="chat-unread-badge">
              {unreadCount} unread
            </span>
          )}
        </div>
      )}

      {!isActivityChannel && unreadCount > 0 && (
        <div className="chat-toolbar">
          <span className="chat-unread-badge">
            {unreadCount} unread
          </span>
        </div>
      )}

      <div className="chat-messages" ref={messagesContainerRef}>
        {loading ? (
          <div className="chat-loading">Loading messages...</div>
        ) : error ? (
          <div className="chat-error">{error}</div>
        ) : groupedMessages.length === 0 ? (
          <div className="chat-empty">{emptyMessage}</div>
        ) : (
          <>
            {hasMore && (
              <div className="chat-load-more" ref={loadMoreSentinelRef}>
                {loadingMore && <span>Loading older messages...</span>}
              </div>
            )}
            {groupedMessages.map((item, index) => {
              if (item.type === 'date') {
                return (
                  <div key={`date-${index}`} className="chat-date-divider">
                    <span>{formatDateLabel(item.date)}</span>
                  </div>
                );
              }
              return renderMessage(item.data);
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {canPost && (
        <>
          {!hideTeamReminder && !isActivityChannel && (
            <div className="chat-team-reminder">
              Please keep conversations project-related. Use the Requests section for formal requests to administrators.
            </div>
          )}
          {allowComments && isActivityChannel && (
            <div className="chat-team-reminder">
              This discussion is open while the application is in Revision Required. It closes when an admin approves or rejects it.
            </div>
          )}
          <div className="chat-input-area">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={commentPlaceholder || (isActivityChannel
                ? 'Add a comment... (Enter to send, Shift+Enter for new line)'
                : 'Type a message... (Enter to send, Shift+Enter for new line)')}
              rows="2"
              disabled={submitting}
            />
            <button
              className="chat-send-btn"
              onClick={handleSend}
              disabled={!newComment.trim() || submitting}
              title="Send message"
            >
              {submitting ? '...' : '➤'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectChat;
